// PagesCards.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
  import { api } from "../pages/users/apiClient";

/**
 * Props:
 *  - apiKey (string)   : required, sent as X-API-Key
 *  - baseURL (string)  : default https://rankridgeschools.com/rankridgeschool/dev/v1
 */
export default function SubPagesCards({
  apiKey="12345678",
  baseURL = "https://gantasoft.in/rankridgeschool/dev/v1",
}) {
  const navigate=useNavigate()
  const api = useMemo(
    () =>
      axios.create({
        baseURL,
        headers: { "X-API-Key": apiKey },
      }),
    [apiKey, baseURL]
  );


  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);

  // expanded subpages: cache per page_id
  // shape: { [page_id]: { loading, error, items: [] } }
  const [subCache, setSubCache] = useState({});
  const [expanded, setExpanded] = useState(new Set()); // which page_ids are open

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/pages", {
        params: { search: q, status, page, limit, sort: "page_name", order: "asc" },
      });
      if (data?.success) {
        setItems(data.items || []);
        setTotal(data.pagination?.total || 0);
      } else {
        setErr(data?.message || "Failed to load");
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e.message || "Load error");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [q, status, page, limit]); // eslint-disable-line
  const pagesCount = Math.max(1, Math.ceil(total / limit));

  function openAdd() {
    setEditing({ page_name: "", page_slug: "", status: "active" });
  }
  function openEdit(it) {
    setEditing({ ...it });
  }
  async function save() {
    if (!editing) return;
    const payload = {
      page_name: editing.page_name,
      page_slug: editing.page_slug,
      status: editing.status,
    };
    if (!payload.page_name || !payload.page_slug) {
      alert("Name and slug required"); return;
    }
    setSaving(true);
    try {
      if (editing.page_id) {
        await api.put(`/pages/${editing.page_id}`, payload);
      } else {
        await api.post(`/pages`, payload, { headers: { "Content-Type": "application/json" } });
      }
      setEditing(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Save failed");
    } finally { setSaving(false); }
  }
  async function onDelete(id) {
    if (!window.confirm("Delete this page?")) return;
    try {
      await api.delete(`/pages/${id}`);
      setItems(prev => prev.filter(x => x.page_id !== id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  }
  async function toggleStatus(id, current) {
    const next = current === "active" ? "inactive" : "active";
    try {
      await api.patch(`/pages/${id}`, { field: "status", value: next });
      setItems(prev => prev.map(x => x.page_id === id ? { ...x, status: next } : x));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Update failed");
    }
  }

  // ----- subpages support -----
  function toggleExpand(pageId) {
    setExpanded(prev => {
      const s = new Set(prev);
      if (s.has(pageId)) s.delete(pageId); else s.add(pageId);
      return s;
    });
    if (!subCache[pageId]) {
      loadSubpages(pageId);
    }
  }
  async function loadSubpages(pageId) {
    setSubCache(prev => ({ ...prev, [pageId]: { ...(prev[pageId]||{}), loading: true, error: "", items: prev[pageId]?.items || [] }}));
    try {
      const { data } = await api.get("/subpages", {
        params: { page_id: pageId, status: "active", sort: "subpage_name", order: "asc", limit: 500 },
      });
      setSubCache(prev => ({ ...prev, [pageId]: { loading: false, error: "", items: data?.items || [] }}));
    } catch (e) {
      setSubCache(prev => ({ ...prev, [pageId]: { loading: false, error: (e?.response?.data?.message || e.message || "Load error"), items: [] }}));
    }
  }
  async function spDelete(subpage_id, page_id) {
    if (!window.confirm("Delete this subpage?")) return;
    try {
      await api.delete(`/subpages/${subpage_id}`);
      setSubCache(prev => ({
        ...prev,
        [page_id]: { ...prev[page_id], items: (prev[page_id]?.items || []).filter(s => s.subpage_id !== subpage_id) }
      }));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Delete failed");
    }
  }
  async function spToggleStatus(subpage_id, current, page_id) {
    const next = current === "active" ? "inactive" : "active";
    try {
      await api.patch(`/subpages/${subpage_id}`, { field: "status", value: next });
      setSubCache(prev => ({
        ...prev,
        [page_id]: {
          ...prev[page_id],
          items: (prev[page_id]?.items || []).map(s => s.subpage_id === subpage_id ? { ...s, status: next } : s)
        }
      }));
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Update failed");
    }
  }

  return (
    <div style={wrap}>
      <h2 style={{ margin: 0 }}>Pages</h2>

      <div style={toolbar}>
        <input
          placeholder="Search name/slug"
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          style={input}
        />
        <select value={status} onChange={(e)=>{ setPage(1); setStatus(e.target.value); }} style={input}>
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select value={limit} onChange={(e)=>{ setPage(1); setLimit(Number(e.target.value)); }} style={input}>
          {[6,12,24,48].map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
        <button onClick={load} style={btn("#1f2937","#fff")}>Refresh</button>
        <button onClick={openAdd} style={btn("#16a34a","#fff")}>Add Page</button>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : err ? (
        <p style={{ color:"#dc2626" }}>{err}</p>
      ) : items.length === 0 ? (
        <div style={emptyBox}>No pages found.</div>
      ) : (
        <>
          <div style={grid}>
            {items.map((it) => {
              const isOpen = expanded.has(it.page_id);
              const sub = subCache[it.page_id];
              return (
                <article key={it.page_id} style={card}>
                  {/* header row (clickable) */}
                  <div
                    onClick={() => toggleExpand(it.page_id)}
                    style={{ display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }}
                    title="Click to show/hide subpages"
                  >
                    <h3 style={{ margin:0 }}>{it.page_name}</h3>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ ...chip, background: it.status==='active'?'#dcfce7':'#fee2e2' }}>
                        {it.status}
                      </span>
                      <span style={{ fontSize:20, color:"#6b7280" }}>{isOpen ? "▾" : "▸"}</span>
                    </div>
                  </div>

                  <div style={{ marginTop:6, fontSize:13 }}>
                    <div><strong>Slug:</strong> <code style={code}>{it.page_slug}</code></div>
                    <div style={{ color:"#6b7280", fontSize:12, marginTop:4 }}>
                      Created: {it.created_date?.replace("T"," ").replace("Z","") || "-"} ·
                      Updated: {it.updated_date?.replace("T"," ").replace("Z","") || "-"}
                    </div>
                  </div>

                  <div style={{ marginTop:10, display:"flex", gap:8 }}>
                    <button onClick={(e)=>{ e.stopPropagation(); openEdit(it); }} style={btn("#2563eb","#fff")}>Edit</button>
                    <button onClick={(e)=>{ e.stopPropagation(); onDelete(it.page_id); }} style={btn("#ef4444","#fff")}>Delete</button>
                    <button onClick={(e)=>{ e.stopPropagation(); toggleStatus(it.page_id,it.status); }} style={btn("#6b7280","#fff")}>
                      {it.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={(e)=>navigate("/pages/"+it.page_id)} style={btn("#2563eb","#fff")}>View</button>


                  </div>

                  {/* expanded subpages panel */}
                  {isOpen && (
                    <div style={subWrap}>
                      {!sub || sub.loading ? (
                        <div style={{ color:"#6b7280" }}>Loading subpages…</div>
                      ) : sub.error ? (
                        <div style={{ color:"#dc2626" }}>{sub.error}</div>
                      ) : sub.items.length === 0 ? (
                        <div style={{ color:"#6b7280" }}>No subpages for this page.</div>
                      ) : (
                        <div style={subGrid}>
                          {sub.items.map(sp => (
                            <div key={sp.subpage_id} style={subCard}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                                <strong>{sp.subpage_name}</strong>
                                <span style={{ ...chip, background: sp.status==='active'?'#dcfce7':'#fee2e2' }}>{sp.status}</span>
                              </div>
                              <div style={{ marginTop:4, fontSize:12 }}>
                                <div><strong>Slug:</strong> <code style={code}>{sp.subpage_slug}</code></div>
                                <div style={{ color:"#6b7280", marginTop:4 }}>
                                  Updated: {sp.updated_date?.replace("T"," ").replace("Z","") || "-"}
                                </div>
                              </div>
                              <div style={{ marginTop:8, display:"flex", gap:8 }}>
                                <button
                                  onClick={(e)=>{ e.stopPropagation(); spToggleStatus(sp.subpage_id, sp.status, it.page_id); }}
                                  style={btn("#6b7280","#fff")}
                                >
                                  {sp.status === "active" ? "Deactivate" : "Activate"}
                                </button>
                                <button
                                  onClick={(e)=>{ e.stopPropagation(); spDelete(sp.subpage_id, it.page_id); }}
                                  style={btn("#ef4444","#fff")}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          <div style={pager}>
            <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} style={btn("#e5e7eb","#111")}>Prev</button>
            <span style={{ fontSize:13 }}>Page {page} / {pagesCount} (total {total})</span>
            <button disabled={page>=pagesCount} onClick={()=>setPage(p=>p+1)} style={btn("#e5e7eb","#111")}>Next</button>
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {editing && (
        <div style={modalBackdrop} onClick={()=>setEditing(null)}>
          <div style={modal} onClick={(e)=>e.stopPropagation()}>
            <h3 style={{ marginTop:0 }}>{editing.page_id ? "Edit Page" : "Add Page"}</h3>
            <div style={{ display:"grid", gap:10, maxHeight:"60vh", overflowY:"auto", paddingRight:8 }}>
              <label style={lbl}>Name
                <input value={editing.page_name||""} onChange={(e)=>setEditing(s=>({...s,page_name:e.target.value}))} style={input}/>
              </label>
              <label style={lbl}>Slug
                <input value={editing.page_slug||""} onChange={(e)=>setEditing(s=>({...s,page_slug:e.target.value}))} style={input}/>
              </label>
              <label style={lbl}>Status
                <select value={editing.status||"active"} onChange={(e)=>setEditing(s=>({...s,status:e.target.value}))} style={input}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </select>
              </label>
            </div>
            <div style={{ display:"flex", gap:8, marginTop:14, justifyContent:"flex-end" }}>
              <button onClick={save} disabled={saving} style={btn("#16a34a","#fff")}>{saving?"Saving…":"Save"}</button>
              <button onClick={()=>setEditing(null)} style={btn("#e5e7eb","#111")}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* styles */
const wrap = { maxWidth: 1100, margin:"0 auto", padding:16 };
const toolbar = { display:"flex", gap:8, flexWrap:"wrap", margin:"12px 0 16px" };
const grid = { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))", gap:12 };
const card = { border:"1px solid #e5e7eb", borderRadius:12, padding:14, background:"#fff" };
const input = { padding:"9px 12px", border:"1px solid #e5e7eb", borderRadius:10 };
const btn = (bg,color)=>({ padding:"7px 12px", background:bg, color, border:"none", borderRadius:8, cursor:"pointer" });
const chip = { fontSize:11, padding:"2px 6px", borderRadius:999 };
const code = { background:"#f3f4f6", padding:"1px 4px", borderRadius:4 };
const emptyBox = { border:"1px dashed #d1d5db", padding:24, borderRadius:12, textAlign:"center", color:"#6b7280" };
const pager = { display:"flex", justifyContent:"center", gap:10, marginTop:16 };
const modalBackdrop = { position:"fixed", inset:0, background:"rgba(0,0,0,.35)", display:"flex", alignItems:"center", justifyContent:"center", padding:16, zIndex:50 };
const modal = { width:"min(500px,100%)", background:"#fff", borderRadius:12, padding:16, boxShadow:"0 8px 20px rgba(0,0,0,.2)" };
const lbl = { display:"grid", gap:6, fontSize:13, color:"#374151" };

const subWrap = { marginTop:12, background:"#fafafa", border:"1px solid #e5e7eb", borderRadius:10, padding:10 };
const subGrid = { display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))", gap:10 };
const subCard = { background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:10 };
